import { useParams } from "react-router-dom";
import { useEffect, useState, useRef, useCallback, memo } from "react";
import Navega from "../componentes/Nav";
import "../estilos/DetallesExperiencia.css";
import { useTranslation } from 'react-i18next';
import { getTour } from "../api/tours";
import BotonWeTravel from "../componentes/BotonWeTravel";
import { API_URL } from "../config";
import { useSeo, resumir } from "../seo";
import { registrarEvento } from "../analytics";

const CLOUD_NAME = 'dndjwrnkq';
const UPLOAD_PRESET = 'pacific_tours';
const API_BASE = API_URL;

const FORM_INICIAL = {
  nombre: "", apellido: "", comentario: "", estrellas: 0, imagenesUrls: []
};

/* ── Estrellas ── */
const StarPicker = memo(({ valor, onChange }) => {
  const [hover, setHover] = useState(0);
  return (
    <div className="estrellas-picker">
      {[1, 2, 3, 4, 5].map(n => (
        <button
          key={n}
          type="button"
          className={`estrella-btn ${n <= (hover || valor) ? "activa" : ""}`}
          onMouseEnter={() => setHover(n)}
          onMouseLeave={() => setHover(0)}
          onClick={() => onChange(n)}
          aria-label={`${n} estrella${n > 1 ? "s" : ""}`}
        >★</button>
      ))}
    </div>
  );
});

/* ── Modal imagen ── */
const ImageModal = memo(({ src, onClose }) => {
  useEffect(() => {
    if (!src) return;
    const handler = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [src, onClose]);

  if (!src) return null;
  return (
    <div className="img-modal-overlay" onClick={onClose}>
      <div className="img-modal" onClick={e => e.stopPropagation()}>
        <button className="img-modal-close" onClick={onClose}>✕</button>
        <img src={src} alt="imagen ampliada" />
      </div>
    </div>
  );
});

/* ── Render estrellas ── */
const RenderEstrellas = memo(({ n = 0 }) => (
  <div className="c-estrellas">
    {[1, 2, 3, 4, 5].map(i => (
      <span key={i} className={i <= n ? "estrella-on" : "estrella-off"}>★</span>
    ))}
  </div>
));

/* ── Tarjeta de comentario ── */
const ComentarioCard = memo(({ c, onImageClick }) => {
  const partes = (c.nombre_usuario || c.nombre || "").trim().split(" ");
  const ini = partes.map(p => p[0] ?? "").join("").toUpperCase().slice(0, 2);
  const imagenUrl = c.imagen || (c.imagenes?.length > 0 ? c.imagenes[0] : null);

  return (
    <div className="comentario-card">
      <div className="comentario-top">
        <div className="comentario-avatar">{ini || "?"}</div>
        <div className="comentario-meta">
          <strong>{c.nombre_usuario || c.nombre}</strong>
          <span>{new Date(c.fecha).toLocaleDateString()}</span>
        </div>
        {c.estrellas > 0 && <RenderEstrellas n={c.estrellas} />}
      </div>
      <p className="comentario-texto">{c.comentario}</p>
      {imagenUrl && (
        <div
          className="comentario-img-thumb"
          onClick={() => onImageClick(imagenUrl)}
          role="button"
          tabIndex={0}
          onKeyDown={e => e.key === 'Enter' && onImageClick(imagenUrl)}
          aria-label="Ver imagen completa"
        >
          <img src={imagenUrl} alt="foto del comentario" />
          <div className="comentario-img-overlay"><span>🔍 Tocá para ampliar</span></div>
        </div>
      )}
    </div>
  );
});

/* ══════════════════════════════════════════════ */
function ExperienciaDetalle() {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();

  const [experiencia, setExperiencia] = useState(null);
  const [comentarios, setComentarios] = useState([]);
  const [errorComentario, setErrorComentario] = useState('');
  const [modalImg, setModalImg] = useState(null);
  const [formAbierto, setFormAbierto] = useState(false);
  const [nuevoComentario, setNuevoComentario] = useState(FORM_INICIAL);
  const [previews, setPreviews] = useState([]);
  const [subiendoImagenes, setSubiendoImagenes] = useState(false);
  const [enviandoComentario, setEnviandoComentario] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [form, setForm] = useState({
    nombre: "", email: "", telefono: "", personas: "", comentario: ""
  });
  const fileInputRef = useRef(null);

  /* ── Cierra modal con Escape ── */
  useEffect(() => {
    if (!openModal) return;
    const handler = (e) => e.key === "Escape" && setOpenModal(false);
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [openModal]);

  /* ── Carga inicial ── */
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      getTour(slug),
      fetch(`${API_BASE}/comentarios/${slug}/`).then(r => r.json())
    ]).then(([tourData, comentariosData]) => {
      if (cancelled) return;
      setExperiencia(tourData);
      setComentarios(Array.isArray(comentariosData) ? comentariosData : []);
    });
    return () => { cancelled = true; };
  }, [slug]);

  /* ── Idioma / precios ── */
  const esIngles = i18n.language === "en";

  const titulo     = esIngles ? experiencia?.title_en    : experiencia?.title;
  const categoria  = esIngles ? experiencia?.category_en : experiencia?.category;
  const duracion   = esIngles ? experiencia?.duration_en : experiencia?.duration;
  const descripcion = esIngles ? experiencia?.description_en : experiencia?.description;
  const preciosActivos = esIngles && experiencia?.pricing_details_en
    ? experiencia.pricing_details_en
    : experiencia?.pricing_details;
  const precioSimple = esIngles && experiencia?.price_en
    ? experiencia.price_en
    : experiencia?.price;

  /* ── SEO: título, descripción y foto propios de este tour ── */
  useSeo({
    pagina: 'detalle',
    params: { slug },
    title: titulo ? `${titulo}${experiencia?.location ? ` - ${experiencia.location}` : ''}` : undefined,
    description: resumir(descripcion),
    image: experiencia?.image,
  });

  /* ── Handlers ── */
  const handleChange = useCallback(
    (e) => setForm(prev => ({ ...prev, [e.target.name]: e.target.value })),
    []
  );

  const setNuevoField = useCallback(
    (campo, valor) => setNuevoComentario(prev => ({ ...prev, [campo]: valor })),
    []
  );

  const enviarWhatsApp = useCallback(() => {
    const mensaje = `Hola! 👋 Quiero reservar una experiencia 🌿

📍 Experiencia: ${titulo}
👤 Nombre: ${form.nombre}
📧 Email: ${form.email}
📞 Teléfono: ${form.telefono}
👥 Personas: ${form.personas}
💬 Comentario: ${form.comentario}`;
    registrarEvento('generate_lead', { tour: slug, metodo: 'whatsapp' });
    window.open(`https://wa.me/50664643174?text=${encodeURIComponent(mensaje)}`, "_blank");
  }, [titulo, form, slug]);

  /* ── Subida de imágenes ── */
  const handleImagenes = useCallback(async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    setSubiendoImagenes(true);
    setErrorComentario('');
    setPreviews(files.map(f => URL.createObjectURL(f)));

    try {
      const urls = await Promise.all(
        files.map(file => {
          const fd = new FormData();
          fd.append('file', file);
          fd.append('upload_preset', UPLOAD_PRESET);
          return fetch(`https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`, {
            method: 'POST', body: fd
          })
            .then(r => r.json())
            .then(d => {
              if (!d.secure_url) throw new Error('No URL from Cloudinary');
              return d.secure_url;
            });
        })
      );
      setNuevoField('imagenesUrls', urls);
    } catch {
      setErrorComentario('Error al subir las imágenes. Intenta de nuevo.');
      setPreviews([]);
    } finally {
      setSubiendoImagenes(false);
    }
  }, [setNuevoField]);

  /* ── Enviar comentario ── */
  const enviarComentario = useCallback(async (e) => {
    e.preventDefault();
    setErrorComentario('');

    const nombreCompleto = `${nuevoComentario.nombre} ${nuevoComentario.apellido}`.trim();
    if (!nombreCompleto || !nuevoComentario.comentario.trim()) {
      setErrorComentario('Por favor completa todos los campos requeridos.');
      return;
    }
    if (nuevoComentario.estrellas === 0) {
      setErrorComentario('Por favor selecciona una puntuación.');
      return;
    }

    setEnviandoComentario(true);
    try {
      const payload = {
        tour_slug: slug,
        /* ✅ FIX: se envía el nombre completo para que el backend lo guarde en nombre_usuario */
        nombre: nombreCompleto,
        comentario: nuevoComentario.comentario.trim(),
        estrellas: nuevoComentario.estrellas,
        imagen: nuevoComentario.imagenesUrls[0] || null,
      };

      const res = await fetch(`${API_BASE}/comentarios/${slug}/`, {
        method: "POST",
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.detail || `Error ${res.status}`);
      }

      const guardado = await res.json();
      setComentarios(prev => [guardado, ...prev]);
      setNuevoComentario(FORM_INICIAL);
      setPreviews([]);
      setFormAbierto(false);
    } catch (err) {
      console.error('Error al enviar comentario:', err);
      setErrorComentario('Ocurrió un error al enviar. Intenta nuevamente.');
    } finally {
      setEnviandoComentario(false);
    }
  }, [nuevoComentario, slug]);

  const cerrarModal = useCallback(() => setModalImg(null), []);

  if (!experiencia) return <h2>Cargando...</h2>;

  const ocupado = subiendoImagenes || enviandoComentario;

  return (
    <>
      <Navega />

      <section className="detalle-experiencia">

        {/* HERO */}
        <div className="detalle-hero">
          <img src={experiencia.image} alt={titulo} />
          <div className="hero-overlay">
            <span className="hero-category">{categoria}</span>
            <h1>{titulo}</h1>
            <p>{experiencia.location}</p>
          </div>
        </div>

        {/* CONTENIDO */}
        <div className="detalle-content">
          <div className="detalle-info">
            <h2>{t("detalle_sobre")}</h2>
            <div className="descripcion">
              {descripcion?.split("\n").filter(p => p.trim()).map((p, i) => <p key={i}>{p}</p>)}
            </div>
            <ul className="detalle-meta">
              <li><strong>{t("detalle_duracion")}:</strong> {duracion}</li>
              <li><strong>{t("detalle_ubicacion")}:</strong> {experiencia.location}</li>
              <li><strong>{t("detalle_categoria")}:</strong> {categoria}</li>
            </ul>
          </div>

          {/* PRECIO */}
          <aside className="detalle-price">
            <div className="price-box">
              {!experiencia.pricing_details && precioSimple && (
                <div className="precio-simple">
                  <span className="precio-label">{t("detalle_precios")}</span>
                  <h3>{precioSimple}</h3>
                </div>
              )}
              {preciosActivos && (
                <>
                  <h3 className="precio-titulo">{t("detalle_precios")}</h3>
                  <ul className="price-list">
                    {preciosActivos.habitaciones.map((hab, i) => {
                      const esCondicion = !hab.precio || hab.precio === "" || hab.precio === null;
                      return (
                        <li key={i} className={esCondicion ? "precio-condicion" : "precio-normal"}>
                          {esCondicion ? (
                            <span className="condicion-texto">⚠️ {hab.tipo} {hab.nota}</span>
                          ) : (
                            <>
                              <span className="precio-tipo">{hab.tipo}</span>
                              <span className="precio-valor">
                                <strong>{hab.precio}</strong>
                                {hab.nota && <small>{hab.nota}</small>}
                              </span>
                            </>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                  {preciosActivos.descuento && (
                    <p className="price-discount">🏷️ {preciosActivos.descuento}</p>
                  )}
                </>
              )}
              {experiencia.wetravel_uuid ? (
                <>
                  <BotonWeTravel
                    uuid={experiencia.wetravel_uuid}
                    texto={t("detalle_btn_reservar_pagar")}
                    className="btn-reservar"
                  />
                  <p className="pago-seguro">🔒 {t("detalle_pago_seguro")}</p>
                </>
              ) : (
                <button className="btn-reservar" onClick={() => setOpenModal(true)}>
                  {t("detalle_btn_reservar")}
                </button>
              )}
            </div>
          </aside>
        </div>

        {/* GALERÍA */}
        <div className="detalle-galeria">
          <h2>{t("detalle_galeria")}</h2>
          <div className="galeria-grid">
            {experiencia.gallery?.map((img, i) => (
              <img
                key={i}
                src={img}
                alt={`Galería ${i + 1}`}
                loading="lazy"
                onClick={() => setModalImg(img)}
                style={{ cursor: "pointer" }}
              />
            ))}
          </div>
        </div>

        {/* ════ COMENTARIOS ════ */}
        <div className="detalle-comentarios">
          <div className="comentarios-header">
            <h2>{t("comentarios_titulo")}</h2>
            <button
              className={`btn-agregar-comentario ${formAbierto ? "abierto" : ""}`}
              onClick={() => setFormAbierto(v => !v)}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" width="16" height="16">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              {t("comentario_abrir")}
            </button>
          </div>

          {/* FORMULARIO */}
          <div className={`comentario-form-wrap ${formAbierto ? "visible" : ""}`}>
            <form onSubmit={enviarComentario} className="comentario-form">
              <div className="form-row-dos">
                <div className="form-field">
                  <label>{t("comentarios_nombre")}</label>
                  <input
                    placeholder={t("comentarios_nombre")}
                    value={nuevoComentario.nombre}
                    onChange={e => setNuevoField('nombre', e.target.value)}
                    required
                  />
                </div>
                <div className="form-field">
                  <label>{t("comentarios_apellido") || "Apellido"}</label>
                  <input
                    placeholder={t("comentarios_apellido") || "Apellido"}
                    value={nuevoComentario.apellido}
                    onChange={e => setNuevoField('apellido', e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="form-field">
                <label>{t("comentarios_puntuacion") || "Puntuación"}</label>
                <StarPicker
                  valor={nuevoComentario.estrellas}
                  onChange={n => setNuevoField('estrellas', n)}
                />
              </div>

              <div className="form-field">
                <label>{t("comentarios_texto")}</label>
                <textarea
                  placeholder={t("comentarios_texto")}
                  value={nuevoComentario.comentario}
                  onChange={e => setNuevoField('comentario', e.target.value)}
                  required
                />
              </div>

              <div className="form-field">
                <label className="label-imagenes">
                  {t("comentarios_imagenes") || "Imágenes"}
                  <span className="badge-opcional">{t("comentarios_opcional")}</span>
                </label>
                <div
                  className="img-upload-area"
                  onClick={() => !subiendoImagenes && fileInputRef.current?.click()}
                  role="button"
                  tabIndex={0}
                  onKeyDown={e => e.key === "Enter" && !subiendoImagenes && fileInputRef.current?.click()}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" width="22" height="22">
                    <rect x="3" y="3" width="18" height="18" rx="4"/>
                    <circle cx="8.5" cy="8.5" r="1.5"/>
                    <polyline points="21 15 16 10 5 21"/>
                  </svg>
                  <span>
                    {subiendoImagenes ? '⏳ Subiendo imágenes...' : t("comentario_tocar_imagen")}
                  </span>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  style={{ display: "none" }}
                  onChange={handleImagenes}
                  disabled={subiendoImagenes}
                />
                {previews.length > 0 && (
                  <div className="img-previews">
                    {previews.map((src, i) => (
                      <img key={i} src={src} alt={`Preview ${i + 1}`} className="img-thumb" />
                    ))}
                  </div>
                )}
              </div>

              {errorComentario && (
                <p style={{ color: '#c0392b', fontSize: '0.9rem', marginTop: '8px' }}>
                  ⚠️ {errorComentario}
                </p>
              )}

              <div className="form-actions">
                <button
                  type="button"
                  className="btn-cancelar"
                  onClick={() => { setFormAbierto(false); setNuevoComentario(FORM_INICIAL); setPreviews([]); }}
                >
                  {t("comentarios_btn_cancelacion")}
                </button>
                <button type="submit" className="btn-publicar" disabled={ocupado}>
                  {enviandoComentario ? '⏳ Enviando...' : subiendoImagenes ? '⏳ Subiendo...' : t("comentarios_btn")}
                </button>
              </div>
            </form>
          </div>

          {/* LISTA */}
          {comentarios.length === 0 ? (
            <p className="comentarios-vacio">{t("comentarios_vacio")}</p>
          ) : (
            <div className="comentarios-lista">
              {comentarios.map(c => (
                <ComentarioCard key={c.id} c={c} onImageClick={setModalImg} />
              ))}
            </div>
          )}
        </div>
      </section>

      <ImageModal src={modalImg} onClose={cerrarModal} />

      {/* MODAL RESERVA */}
      {openModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setOpenModal(false)}>
          <div className="modal">
            <button className="modal-close" onClick={() => setOpenModal(false)}>✕</button>
            <div className="modal-header">
              <h2>{t("modal_titulo")}</h2>
              <p className="modal-exp">📍 {titulo}</p>
            </div>
            <div className="modal-body">
              {[
                { name: "nombre",   label: `👤 ${t("modal_nombre")}`,      type: "text",   placeholder: t("modal_nombre") },
                { name: "email",    label: "📧 Email",                      type: "email",  placeholder: "correo@ejemplo.com" },
                { name: "telefono", label: `📞 ${t("modal_telefono")}`,    type: "text",   placeholder: "+506 0000 0000" },
                { name: "personas", label: `👥 ${esIngles ? "Number of people" : "Número de personas"}`, type: "number", placeholder: "1", min: "1" },
              ].map(f => (
                <div key={f.name} className="modal-field">
                  <label>{f.label}</label>
                  <input name={f.name} type={f.type} placeholder={f.placeholder} min={f.min} onChange={handleChange} required />
                </div>
              ))}
              <div className="modal-field">
                <label>💬 {t("modal_comentario")}</label>
                <textarea name="comentario" placeholder={t("modal_comentario")} onChange={handleChange} rows="3" />
              </div>
            </div>
            <button className="btn-whatsapp" onClick={enviarWhatsApp}>
              <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
              </svg>
              {t("modal_btn")}
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default ExperienciaDetalle;